import { Currency, CurrencySettings, CurrencyUtil, ModelRow } from "@mcleod/core";

const READY_TO_PAY = "Y";
const HOLD = "N";

export class PayeeData {

    protected settlements: Map<string, ModelRow>;
    protected adjustments: Map<string, ModelRow>;
    protected payeeRow: ModelRow;
    protected lastSettlementTotal: Currency = new Currency(0);
    protected lastPayAdjustmentTotal: Currency = new Currency(0);
    protected lastTimecardTotal: Currency = new Currency(0);
    protected totalsLastChangedDateTime: Date | null = null;
    protected cachedApiResponse: ModelRow | null = null;
    protected _hasTaxUpdates: boolean = false;

    constructor() {
        this.settlements = new Map<string, ModelRow>();
        this.adjustments = new Map<string, ModelRow>();
    }

    public addSettlement(key: string, settlement: ModelRow): void {
        this.settlements.set(key, settlement);
    }

    public addAdjustment(key: string, adjustment: ModelRow): void {
        this.adjustments.set(key, adjustment);
    }

    public addTimecard(modelPath: string, checkPayeeType: boolean = false): ModelRow {
        if (checkPayeeType && this.payeeRow.get("payee_type") !== 'C') return;
        const timecardRow = new ModelRow(modelPath, true);
        const payeeCurrency = this.payeeRow.get("payee_currency", CurrencySettings.getFunctionalCurrency());

        // Identity
        timecardRow.set("id", this.payeeRow.get("payee_id"));

        // Dates
        timecardRow.set("pay_method", this.payeeRow.get("pay_method"));
        timecardRow.set("terminal_id", this.payeeRow.get("terminal_id"));
        timecardRow.set("division_id", this.payeeRow.get("division_id"));
        timecardRow.set("job_class_code_id", this.payeeRow.get("job_class_code_id"));
        timecardRow.set("department", this.payeeRow.get("department"));

        // External fields
        timecardRow.set("payee_name", this.payeeRow.get("payee_name"));
        timecardRow.set("terminal_descr", this.payeeRow.get("terminal_descr"));
        timecardRow.set("division_descr", this.payeeRow.get("division_descr"));
        timecardRow.set("job_class_code_descr", this.payeeRow.get("job_class_code_descr"));
        timecardRow.set("payee_process_status", this.payeeRow.get("process_status"));
        timecardRow.set("overtime_cutoff", this.payeeRow.get("overtime_cutoff"));
        timecardRow.set("sick_leave_balance", this.payeeRow.get("sick_leave_balance"));
        timecardRow.set("vacation_leave_balance", this.payeeRow.get("vacation_leave_balance"));
        timecardRow.set("payee_currency", payeeCurrency);

        timecardRow.set("regular_units", 0);
        timecardRow.set("overtime_units", 0);
        timecardRow.set("vacation_units", 0);
        timecardRow.set("sick_units", 0);
        timecardRow.set("holiday_units", 0);
        timecardRow.set("total_hours", 0);

        timecardRow.set("regular_total", new Currency({ amount: 0, currency_code: payeeCurrency }));
        timecardRow.set("overtime_total", new Currency({ amount: 0, currency_code: payeeCurrency }));
        timecardRow.set("vacation_total", new Currency({ amount: 0, currency_code: payeeCurrency }));
        timecardRow.set("sick_total", new Currency({ amount: 0, currency_code: payeeCurrency }));
        timecardRow.set("holiday_total", new Currency({ amount: 0, currency_code: payeeCurrency }));
        timecardRow.set("total_pay", new Currency({ amount: 0, currency_code: payeeCurrency }));
        return timecardRow;
    }

    public getTimecard(modelPath: string, checkPayeeType: boolean = false): ModelRow | null {
        if (!this.payeeRow) return null;
        if (checkPayeeType && this.payeeRow.get("payee_type") !== 'C') return null;
        if (this.payeeRow && !this.payeeRow.isNull("tc_payee_id")) {
            const timecardRow = new ModelRow(modelPath, false);
            const payeeCurrency = this.payeeRow.get("payee_currency", CurrencySettings.getFunctionalCurrency());

            // Identity
            timecardRow.set("id", this.payeeRow.get("tc_payee_id"));

            // Dates
            timecardRow.set("payperiod_start", this.payeeRow.get("tc_payperiod_start"));
            timecardRow.set("payperiod_end", this.payeeRow.get("tc_payperiod_end"));
            timecardRow.set("check_date", this.payeeRow.get("tc_check_date"));

            // Rates
            timecardRow.set("regular_rate", this.payeeRow.get("tc_regular_rate"));
            timecardRow.set("overtime_rate", this.payeeRow.get("tc_overtime_rate"));
            timecardRow.set("vacation_rate", this.payeeRow.get("tc_vacation_rate"));

            // Units
            timecardRow.set("regular_units", this.payeeRow.get("tc_regular_units", 0));
            timecardRow.set("overtime_units", this.payeeRow.get("tc_overtime_units", 0));
            timecardRow.set("vacation_units", this.payeeRow.get("tc_vacation_units", 0));
            timecardRow.set("sick_units", this.payeeRow.get("tc_sick_units", 0));
            timecardRow.set("holiday_units", this.payeeRow.get("tc_holiday_units", 0));
            timecardRow.set("total_hours", this.payeeRow.get("tc_total_hours", 0));
            timecardRow.set("addl_tax_override_hours", this.payeeRow.get("tc_addl_tax_override_hours"));

            // Totals (Currency)
            timecardRow.set("payee_currency", payeeCurrency);
            const tcRegularTotal = this.payeeRow.getCurrency("tc_regular_total", new Currency(0));
            tcRegularTotal.setCurrencyCode(payeeCurrency);
            const tcOvertimeTotal = this.payeeRow.getCurrency("tc_overtime_total", new Currency(0));
            tcOvertimeTotal.setCurrencyCode(payeeCurrency);
            const tcVacationTotal = this.payeeRow.getCurrency("tc_vacation_total", new Currency(0));
            tcVacationTotal.setCurrencyCode(payeeCurrency);
            const tcSickTotal = this.payeeRow.getCurrency("tc_sick_total", new Currency(0));
            tcSickTotal.setCurrencyCode(payeeCurrency);
            const tcHolidayTotal = this.payeeRow.getCurrency("tc_holiday_total", new Currency(0));
            tcHolidayTotal.setCurrencyCode(payeeCurrency);
            const tcTotalPay = this.payeeRow.getCurrency("tc_total_pay", new Currency(0));
            tcTotalPay.setCurrencyCode(payeeCurrency);
            timecardRow.set("regular_total", tcRegularTotal);
            timecardRow.set("overtime_total", tcOvertimeTotal);
            timecardRow.set("vacation_total", tcVacationTotal);
            timecardRow.set("sick_total", tcSickTotal);
            timecardRow.set("holiday_total", tcHolidayTotal);
            timecardRow.set("total_pay", tcTotalPay);

            // Status/Check Info
            timecardRow.set("payee_process_status", this.payeeRow.get("tc_process_status"));
            timecardRow.set("check_number", this.payeeRow.get("tc_check_number"));
            timecardRow.set("pay_method", this.payeeRow.get("pay_method", this.payeeRow.get("tc_pay_method")));
            timecardRow.set("terminal_id", this.payeeRow.get("terminal_id"));
            timecardRow.set("division_id", this.payeeRow.get("division_id"));
            timecardRow.set("job_class_code_id", this.payeeRow.get("job_class_code_id"));
            timecardRow.set("department", this.payeeRow.get("department"));

            // External fields
            timecardRow.set("payee_name", this.payeeRow.get("payee_name"));
            timecardRow.set("terminal_descr", this.payeeRow.get("terminal_descr"));
            timecardRow.set("division_descr", this.payeeRow.get("division_descr"));
            timecardRow.set("job_class_code_descr", this.payeeRow.get("job_class_code_descr"));
            timecardRow.set("payee_process_status", this.payeeRow.get("process_status"));
            timecardRow.set("overtime_cutoff", this.payeeRow.get("overtime_cutoff"));
            timecardRow.set("sick_leave_balance", this.payeeRow.get("sick_leave_balance"));
            timecardRow.set("vacation_leave_balance", this.payeeRow.get("vacation_leave_balance"));
            return timecardRow;
        }
        return null;
    }

    addPayeeRow(row: ModelRow): void {
        this.payeeRow = row;
    }

    getPayeeRow(): ModelRow {
        return this.payeeRow;
    }

    set hasTaxUpdates(value: boolean) {
        this._hasTaxUpdates = value;
    }

    get hasTaxUpdates(): boolean {
        return this._hasTaxUpdates;
    }

    public getReadyToPaySettlements(): ModelRow[] {
        return Array.from(this.settlements.values()).filter(row => row.get("ready_to_pay_flag") === READY_TO_PAY);
    }

    public getReadyToPayAdjustments(): ModelRow[] {
        return Array.from(this.adjustments.values()).filter(row => row.get("ready_to_pay_flag") === READY_TO_PAY);
    }

    public findPayableById(id: string): ModelRow | null {
        const settlement = this.settlements.get(id);
        if (settlement) {
            return settlement;
        }
        const adjustment = this.adjustments.get(id);
        if (adjustment) {
            return adjustment;
        }
        return null;
    }

    public getAllPayables(): ModelRow[] {
        const settlements = Array.from(this.settlements.values());
        const adjustments = Array.from(this.adjustments.values());
        return [...settlements, ...adjustments];
    }

    public async calculateSettlementProcessTotal(): Promise<Currency> {
        const total: Currency = new Currency(0);
        for (const row of this.getReadyToPaySettlements()) {
            const payAmount = row.getCurrency("pay_amount");
            if (payAmount) {
                await total.add(payAmount);
            }
        }
        return total;
    }

    public async calculatePayAdjustmentProcessTotal(): Promise<Currency> {
        const total: Currency = new Currency(0);
        for (const row of this.getReadyToPayAdjustments()) {
            const payAmount = row.getCurrency("pay_amount");
            if (payAmount) {
                await total.add(payAmount);
            }
        }
        return total;
    }

    public calculateTimecardTotal(): Currency {
        return this.payeeRow ? this.payeeRow.getCurrency("tc_total_pay", new Currency(0)) : new Currency(0);
    }

    public async hasChanges(updateTotals: boolean = true): Promise<boolean> {
        return await this.checkForTotalChanges(updateTotals) || this.hasTaxUpdates;
    }

    public async checkForTotalChanges(updateTotals: boolean = true): Promise<boolean> {
        const currentSettlementTotal = await this.calculateSettlementProcessTotal();
        const currentPayAdjustmentTotal = await this.calculatePayAdjustmentProcessTotal();
        const currentTimecardTotal = this.calculateTimecardTotal();

        const hasChanges = !CurrencyUtil.currenciesAreEqual(currentTimecardTotal, this.lastTimecardTotal) || !CurrencyUtil.currenciesAreEqual(currentSettlementTotal, this.lastSettlementTotal) || !CurrencyUtil.currenciesAreEqual(currentPayAdjustmentTotal, this.lastPayAdjustmentTotal);

        if (hasChanges && updateTotals) {
            this.lastSettlementTotal = currentSettlementTotal.getClone(currentSettlementTotal.getAmount());
            this.lastPayAdjustmentTotal = currentPayAdjustmentTotal.getClone(currentPayAdjustmentTotal.getAmount());
            this.lastTimecardTotal = currentTimecardTotal.getClone(currentTimecardTotal.getAmount());
            this.totalsLastChangedDateTime = new Date();
            // Clear cached API response since totals have changed
            this.cachedApiResponse = null;
        }

        return hasChanges;
    }

    public async setTotals() {
        const currentSettlementTotal = await this.calculateSettlementProcessTotal();
        const currentPayAdjustmentTotal = await this.calculatePayAdjustmentProcessTotal();
        const currentTimecardTotal = this.calculateTimecardTotal();
        this.lastSettlementTotal = currentSettlementTotal.getClone(currentSettlementTotal.getAmount());
        this.lastPayAdjustmentTotal = currentPayAdjustmentTotal.getClone(currentPayAdjustmentTotal.getAmount());
        this.lastTimecardTotal = currentTimecardTotal.getClone(currentTimecardTotal.getAmount());
        this.totalsLastChangedDateTime = new Date();
    }

    public getTotalsLastChangedDateTime(): Date | null {
        return this.totalsLastChangedDateTime;
    }

    public getCachedApiResponse(): ModelRow | null {
        return this.cachedApiResponse;
    }

    public setCachedApiResponse(response: ModelRow | null): void {
        this.cachedApiResponse = response;
    }

    public hasCachedApiResponse(): boolean {
        return this.cachedApiResponse !== null;
    }

    public resetTotalsChanged(): void {
        this.totalsLastChangedDateTime = null;
    }

    public removeSettlement(key: string): void {
        this.settlements.delete(key);
    }

    public removeAdjustment(key: string): void {
        this.adjustments.delete(key);
    }
}

export class PayeeDataLookup<T extends PayeeData> {
    private payeeData: Map<string, T>;
    private readonly payeeDataFactory: () => T;

    constructor(payeeDataFactory: () => T) {
        this.payeeData = new Map<string, T>();
        this.payeeDataFactory = payeeDataFactory;
    }

    addPayable(row: ModelRow) {
        const key = row.get("payee_id");
        const payeeData = this.payeeData.get(key) || this.payeeDataFactory();

        if (row.get("record_type") === "S") {
            payeeData.addSettlement(row.get("record_id"), row);
        }
        if (row.get("record_type") === "P") {
            payeeData.addAdjustment(row.get("record_id"), row);
        }
        if (row.get("record_type") === "O") {
            payeeData.addAdjustment(row.get("record_id"), row);
        }
        this.payeeData.set(key, payeeData);
    }

    addPayeeRow(row: ModelRow) {
        const key = row.get("payee_id");
        const payeeData = this.payeeData.get(key) || this.payeeDataFactory();
        payeeData.addPayeeRow(row);
        this.payeeData.set(key, payeeData);
    }

    getPayeeData(payeeId: string): T {
        return this.payeeData.get(payeeId);
    }

    size(): number {
        return this.payeeData.size;
    }

    getAllPayeeData(): T[] {
        return Array.from(this.payeeData.values());
    }

    resetAllTotalsChangedFlags(): void {
        for (const payeeData of this.payeeData.values()) {
            payeeData.resetTotalsChanged();
        }
    }

    updatePayable(row: ModelRow) {
        const key = row.get("payee_id");
        const payeeData = this.payeeData.get(key);

        if (payeeData) {
            if (row.get("record_type") === "S") {
                payeeData.addSettlement(row.get("record_id"), row);
            } else if (row.get("record_type") === "P") {
                payeeData.addAdjustment(row.get("record_id"), row);
            }
            else if (row.get("record_type") === "O") {
                payeeData.addAdjustment(row.get("record_id"), row);
            }
        }
    }

    deletePayable(row: ModelRow) {
        const key = row.get("payee_id");
        const payeeData = this.payeeData.get(key);

        if (payeeData) {
            const recordId = row.get("record_id");
            if (row.get("record_type") === "S") {
                payeeData.removeSettlement(recordId);
            } else if (row.get("record_type") === "P") {
                payeeData.removeAdjustment(recordId);
            }
            else if (row.get("record_type") === "O") {
                payeeData.removeAdjustment(recordId);
            }
        }
    }

    deletePayeeRow(row: ModelRow) {
        const key = row.get("payee_id");
        const payeeData = this.payeeData.get(key);

        if (payeeData) {
            payeeData.addPayeeRow(null); // Clear the payee row
            this.payeeData.delete(key); // Optionally remove the payee data if no longer needed
        }
    }
}

// Settlement-specific PayeeData subclass
export class SettlementPayeeData extends PayeeData {
    constructor() {
        super();
    }

    public addTimecard(): ModelRow {
        return super.addTimecard("lme/settlement/drs-timecard", true);
    }

    public getTimecard(): ModelRow | null {
        return super.getTimecard("lme/settlement/drs-timecard", true);
    }
}

// Office-specific PayeeData subclass
export class OfficePayeeData extends PayeeData {
    constructor() {
        super();
    }

    public addTimecard(): ModelRow {
        return super.addTimecard("lme/backoffice/officepayroll/timecard", false);
    }

    public getTimecard(): ModelRow | null {
        return super.getTimecard("lme/backoffice/officepayroll/timecard", false);
    }
}

// Type aliases for backward compatibility
export type SettlementPayeeDataLookup = PayeeDataLookup<SettlementPayeeData>;
export type OfficePayeeDataLookup = PayeeDataLookup<OfficePayeeData>;
